// C1.3 — analytics auto-refresh policy.
//
// The heavy analytics sections (station×config matrix, latitude×|dR| scatter,
// convergence matrix) are large to compute and to paint, so they must NOT
// refetch on the frequent 5s status poll or on a blind 30s timer. They refresh
// only when there is genuinely new data: the moment a running series finishes
// (active → terminal), i.e. when fresh residuals have just landed. Mount,
// filter change and the manual button remain the other refresh triggers —
// those come from React Query defaults + the screen's own handlers.
//
// The 5s status poll stays a SEPARATE ['status'] query — it never fans out to
// the ['analytics'] namespace — so the light poll cadence and the heavy
// analytics cadence are decoupled (this replaces the old 30s
// invalidateQueries(['analytics']) timer that coupled them).

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useActiveJobsIndicator } from './useActiveJobsIndicator';

export function useAnalyticsAutoRefresh(): { hasActiveJobs: boolean } {
  const queryClient = useQueryClient();
  const { hasActiveJobs } = useActiveJobsIndicator();
  // Previous poll's active flag, so we fire exactly on the active→terminal edge
  // — never on a steady-state poll, never on the terminal→active edge.
  const wasActiveRef = useRef(false);

  useEffect(() => {
    const wasActive = wasActiveRef.current;
    wasActiveRef.current = hasActiveJobs;
    if (wasActive && !hasActiveJobs) {
      // The series just finished — pull the fresh analytics cohort exactly once.
      void queryClient.invalidateQueries({ queryKey: ['analytics'] });
    }
  }, [hasActiveJobs, queryClient]);

  return { hasActiveJobs };
}
